import { useQuery } from '@tanstack/react-query';
import { useMemo } from 'react';
import { CACHE_CONFIG, RETRY_CONFIG } from '../constants/endpoints';

interface DropdownDataConfig<T> {
  endpoint: string;
  getId: (item: T) => number;
  getDisplayName: (item: T) => string;
  getSubtext?: (item: T) => string;
  staleTime?: number;
}

interface DropdownDataParams {
  searchQuery: string;
  currentPage: number;
  itemsPerPage: number;
}

interface ApiResponse<T> {
  data?: T[];
  experiences?: T[];
  items?: T[];
}

interface DropdownDataError extends Error {
  status?: number;
  code?: string;
}

const normalizeApiResponse = <T>(result: unknown): T[] => {
  if (Array.isArray(result)) {
    return result;
  }

  if (result && typeof result === 'object') {
    const response = result as ApiResponse<T>;
    return response.data || response.experiences || response.items || [];
  }

  return [];
};

const createDropdownError = (message: string, status?: number): DropdownDataError => {
  const error = new Error(message) as DropdownDataError;
  error.status = status;
  error.name = 'DropdownDataError';
  return error;
};

export function useDropdownData<T>(config: DropdownDataConfig<T>, params: DropdownDataParams) {
  const {
    endpoint,
    getId,
    getDisplayName,
    getSubtext,
    staleTime = CACHE_CONFIG.DROPDOWN.staleTime,
  } = config;
  const { searchQuery, currentPage, itemsPerPage } = params;

  const { data, isLoading, error } = useQuery<T[], DropdownDataError>({
    queryKey: ['dropdown-data', endpoint],
    queryFn: async () => {
      try {
        const response = await fetch(endpoint, { credentials: 'include' });

        if (!response.ok) {
          throw createDropdownError(`Failed to fetch data from ${endpoint}`, response.status);
        }

        const result = await response.json();
        return normalizeApiResponse<T>(result);
      } catch (err) {
        if (err instanceof Error) {
          throw createDropdownError(err.message);
        }
        throw createDropdownError('Unknown error occurred while fetching data');
      }
    },
    staleTime,
    retry: RETRY_CONFIG.shouldRetry,
    retryDelay: RETRY_CONFIG.retryDelay,
  });

  const processedData = useMemo(() => {
    if (!data) {
      return { items: [], totalPages: 1, totalCount: 0 };
    }

    let filteredItems = data;

    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase();
      filteredItems = data.filter((item: T) => {
        const name = getDisplayName(item).toLowerCase();
        const subtext = getSubtext?.(item)?.toLowerCase() || '';
        return name.includes(query) || subtext.includes(query);
      });
    }

    const startIndex = (currentPage - 1) * itemsPerPage;
    const paginatedItems = filteredItems.slice(startIndex, startIndex + itemsPerPage);
    const totalPages = Math.ceil(filteredItems.length / itemsPerPage);

    return {
      items: paginatedItems,
      totalPages: Math.max(1, totalPages),
      totalCount: filteredItems.length,
    };
  }, [data, searchQuery, currentPage, itemsPerPage, getDisplayName, getSubtext]);

  const getItemName = useMemo(() => {
    if (!data) return (id: number) => `Item ${id}`;

    const itemMap = new Map<number, string>();
    data.forEach((item: T) => {
      itemMap.set(getId(item), getDisplayName(item));
    });

    return (id: number) => itemMap.get(id) || `Item ${id}`;
  }, [data, getId, getDisplayName]);

  return {
    items: processedData.items,
    totalPages: processedData.totalPages,
    totalCount: processedData.totalCount,
    isLoading,
    error,
    getItemName,
  };
}
